import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import Navbar from "../components/Navbar";
import { useAuth } from "../context/AuthContext";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import { NbButton, NbInput } from "../components/nb";
import { Crown, Lock, CreditCard, Check, Truck } from "lucide-react";

export default function Upgrade() {
  const { user, setUser } = useAuth();
  const navigate = useNavigate();
  const [card, setCard] = useState({ card_number: "", exp: "", cvv: "", name_on_card: "" });
  const [loading, setLoading] = useState(false);
  const [plan, setPlan] = useState("premium_monthly");

  const PLANS_FE = {
    premium_monthly: { name: "Monthly", price: "$8.50", per: "/ month", amount: "$8.50", note: "Cancel anytime." },
    premium_6mo: { name: "6 Months", price: "$40", per: "/ 6 months", amount: "$40.00", note: "Save ~22% vs monthly." },
  };

  if (user.membership === "premium") {
    return (
      <div className="min-h-screen bg-[#FCFCFA]"><Navbar />
        <div className="max-w-md mx-auto px-4 py-16 text-center">
          <div className="bg-white rounded-3xl border border-stone-100 shadow-[0_16px_50px_rgb(28,25,23,0.08)] p-10">
            <div className="w-14 h-14 rounded-full bg-[#F3E8E6] flex items-center justify-center mx-auto mb-4"><Crown className="w-7 h-7 text-[#D4AF37]" /></div>
            <h1 className="font-display text-3xl font-medium mb-2 text-stone-900">You're Premium</h1>
            <p className="text-stone-600 mb-2">Renews on {user.membership_expires ? new Date(user.membership_expires).toLocaleDateString() : "—"}</p>
            <div className="flex flex-col gap-2 mt-6">
              <NbButton variant="outline" onClick={() => navigate("/settings")}>Manage Subscription</NbButton>
              <NbButton as={Link} to="/order-cards" variant="accent"><Truck className="w-4 h-4" /> Order Printed Cards</NbButton>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/subscribe", { plan, ...card });
      setUser((u) => ({ ...u, ...data.user }));
      toast.success("Payment successful! Welcome to Premium 🎉");
      navigate("/dashboard");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setLoading(false); }
  };

  const features = ["See who likes you", "Advanced filters", "Unlimited messaging", "Read receipts", "Custom badge themes", "Verified badge"];

  return (
    <div className="min-h-screen bg-[#FCFCFA]">
      <Navbar />
      <div className="max-w-4xl mx-auto px-6 sm:px-8 py-10">
        <h1 className="font-display text-4xl font-medium tracking-tight text-stone-900 mb-1 flex items-center gap-2"><Crown className="w-7 h-7 text-[#D4AF37]" /> Go Premium</h1>
        <p className="text-stone-600 mb-8">Everything qrcupid offers, from just $8.50 a month.</p>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-white rounded-2xl border border-stone-100 shadow-[0_8px_30px_rgb(28,25,23,0.05)] p-7 h-fit">
            <div className="space-y-3 mb-6">
              {Object.entries(PLANS_FE).map(([key, p]) => (
                <button
                  key={key}
                  type="button"
                  onClick={() => setPlan(key)}
                  data-testid={`select-plan-${key}`}
                  className={`w-full text-left rounded-xl border p-4 transition-all ${plan === key ? "border-[#8B1E3F] bg-[#FBF4F2] ring-1 ring-[#8B1E3F]/20" : "border-stone-200 hover:border-stone-300"}`}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="font-medium text-stone-900">{p.name}</div>
                      <div className="text-xs text-stone-500">{p.note}</div>
                    </div>
                    <div className="text-right">
                      <span className="font-display text-2xl font-medium text-stone-900">{p.price}</span>
                      <span className="text-xs text-stone-500 ml-1">{p.per}</span>
                    </div>
                  </div>
                </button>
              ))}
            </div>
            {features.map((f) => (
              <div key={f} className="flex items-center gap-2 text-sm text-stone-700 py-1.5"><Check className="w-4 h-4 text-[#8B1E3F]" strokeWidth={3} /> {f}</div>
            ))}
          </div>

          <form onSubmit={submit} className="bg-white rounded-2xl border border-stone-100 shadow-[0_8px_30px_rgb(28,25,23,0.05)] p-7 space-y-4 h-fit">
            <div className="flex items-center gap-2 font-display text-xl font-medium text-stone-900"><CreditCard className="w-5 h-5" /> Payment Details</div>
            <div className="bg-stone-50 border border-stone-200 rounded-xl p-2.5 text-xs text-stone-500 flex items-center gap-1.5"><Lock className="w-3 h-3" /> Secured by Authorize.Net (sandbox)</div>
            <div><label className="text-sm font-medium text-stone-700">Name on Card</label><NbInput data-testid="card-name" required value={card.name_on_card} onChange={(e) => setCard({ ...card, name_on_card: e.target.value })} className="mt-1.5" /></div>
            <div><label className="text-sm font-medium text-stone-700">Card Number</label><NbInput data-testid="card-number" required value={card.card_number} onChange={(e) => setCard({ ...card, card_number: e.target.value })} className="mt-1.5" placeholder="4111 1111 1111 1111" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-sm font-medium text-stone-700">Expiry</label><NbInput data-testid="card-exp" required value={card.exp} onChange={(e) => setCard({ ...card, exp: e.target.value })} className="mt-1.5" placeholder="MM/YY" /></div>
              <div><label className="text-sm font-medium text-stone-700">CVV</label><NbInput data-testid="card-cvv" required value={card.cvv} onChange={(e) => setCard({ ...card, cvv: e.target.value })} className="mt-1.5" placeholder="123" /></div>
            </div>
            <NbButton type="submit" disabled={loading} className="w-full" data-testid="pay-submit">{loading ? "Processing..." : `Pay ${PLANS_FE[plan].amount} ${PLANS_FE[plan].per}`}</NbButton>
            <p className="text-xs text-stone-400 text-center">Test card: 4111 1111 1111 1111</p>
          </form>
        </div>
      </div>
    </div>
  );
}
